import React, { useState, useEffect, useRef } from 'react';
import { getWAStatus, getWAMessages, sendWATest, disconnectWA, getTechniciens, getWATemplates, updateWATemplate } from '../services/api';

function normalizePhone(raw) {
  const digits = (raw || '').replace('@s.whatsapp.net', '').replace(/\D/g, '');
  if (digits.startsWith('33') && digits.length === 11) return '0' + digits.slice(2);
  if (digits.startsWith('0') && digits.length === 10) return digits;
  return digits;
}
function buildPhoneMap(techniciens) {
  const map = {};
  (techniciens || []).forEach(t => {
    if (!t.telephone) return;
    const key = normalizePhone(t.telephone);
    if (key) map[key] = `${t.prenom || ''} ${t.nom || ''}`.trim();
  });
  return map;
}
function fmtDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d)) return '—';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function isToday(ts) {
  if (!ts) return false;
  return new Date(ts).toDateString() === new Date().toDateString();
}

const MSG_STATUS = {
  sent:   { label: 'Envoyé',     bg: '#f0fdf4', color: '#15803d', border: '#86efac' },
  queued: { label: 'En attente', bg: '#fefce8', color: '#b45309', border: '#fde68a' },
  error:  { label: 'Erreur',     bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
};

const VARIABLES = [
  { key: '{{ref_commande}}',       desc: 'Référence commande' },
  { key: '{{client_final}}',       desc: 'Nom du client' },
  { key: '{{adresse}}',            desc: 'Adresse complète' },
  { key: '{{date}}',               desc: 'Date intervention' },
  { key: '{{heure}}',              desc: 'Heure (préfixée " à ")' },
  { key: '{{typo}}',               desc: 'Type raccordement (entre [ ])' },
  { key: '{{motif_rejet}}',        desc: 'Motif du rejet (CR rejeté)' },
  { key: '{{commentaire_covage}}', desc: 'Commentaire Covage' },
];

// ── MsgCard ───────────────────────────────────────────────────────────────────
function MsgCard({ msg, phoneMap, onResend }) {
  const [open,      setOpen]      = useState(false);
  const [resending, setResending] = useState(false);
  const [resendOk,  setResendOk]  = useState(false);
  const normalized = normalizePhone(msg.to || '');
  const techName   = phoneMap?.[normalized];
  const cfg        = MSG_STATUS[msg.status] || MSG_STATUS.sent;
  const canResend  = msg.status === 'queued' || msg.status === 'error';

  const handleResendClick = async (e) => {
    e.stopPropagation();
    setResending(true); setResendOk(false);
    try {
      await onResend(msg);
      setResendOk(true);
      setTimeout(() => setResendOk(false), 3000);
    } catch {}
    setResending(false);
  };

  return (
    <div onClick={() => setOpen(o => !o)} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
            {techName && <span style={{ fontSize: 13, fontWeight: 700 }}>{techName}</span>}
            <span style={{ fontSize: 11, fontFamily: 'monospace', color: techName ? 'var(--text3)' : 'var(--accent)', fontWeight: techName ? 400 : 700 }}>
              {normalized || msg.to || '—'}
            </span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
              {cfg.label}
            </span>
            {isToday(msg.ts) && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}>Aujourd'hui</span>}
            {resendOk && <span style={{ fontSize: 10, color: '#15803d', fontWeight: 600 }}>✓ Envoyé</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: open ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: open ? 'unset' : 'ellipsis' }}>
            {msg.text || '—'}
          </div>
          {open && msg.error && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>Erreur : {msg.error}</div>}
        </div>
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {canResend && (
            <button onClick={handleResendClick} disabled={resending}
              style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {resending ? '…' : '↻ Renvoyer'}
            </button>
          )}
          <div style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{fmtDate(msg.ts)}</div>
        </div>
      </div>
    </div>
  );
}

// ── TemplateCard ──────────────────────────────────────────────────────────────
function TemplateCard({ tpl, onSave }) {
  const [editing, setEditing]   = useState(false);
  const [value, setValue]       = useState(tpl.template);
  const [saving, setSaving]     = useState(false);
  const [result, setResult]     = useState('');

  const handleSave = async () => {
    setSaving(true); setResult('');
    try {
      await onSave(tpl.id, value);
      setResult('✓ Sauvegardé');
      setEditing(false);
    } catch { setResult('✗ Erreur lors de la sauvegarde'); }
    setSaving(false);
  };

  const handleReset = () => { setValue(tpl.template); setEditing(false); setResult(''); };

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{tpl.nom}</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
            <span style={{ fontWeight: 600, color: 'var(--accent)' }}>Déclencheur :</span> {tpl.declencheur}
            {'  ·  '}
            <span style={{ fontWeight: 600, color: 'var(--accent)' }}>Fréquence :</span> {tpl.frequence}
          </div>
        </div>
        <button onClick={() => { setEditing(e => !e); setResult(''); }}
          style={{ padding: '5px 14px', borderRadius: 8, border: '1px solid var(--border)', background: editing ? '#fef2f2' : 'var(--bg3)', color: editing ? '#b91c1c' : 'var(--text2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {editing ? '✕ Annuler' : '✏ Modifier'}
        </button>
      </div>

      {/* Template */}
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <textarea
            value={value}
            onChange={e => setValue(e.target.value)}
            rows={6}
            style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--accent)', borderRadius: 8, fontSize: 13, fontFamily: 'monospace', background: 'var(--bg)', color: 'var(--text)', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box' }}
          />
          <div style={{ fontSize: 11, color: 'var(--text3)', background: 'var(--bg3)', padding: '8px 12px', borderRadius: 8 }}>
            <strong>Variables disponibles :</strong>{' '}
            {VARIABLES.map(v => (
              <span key={v.key} onClick={() => setValue(val => val + v.key)}
                title={v.desc}
                style={{ display: 'inline-block', margin: '2px 3px', padding: '1px 7px', background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, cursor: 'pointer', fontFamily: 'monospace', fontSize: 11 }}>
                {v.key}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {saving ? 'Sauvegarde…' : '✓ Sauvegarder'}
            </button>
            <button onClick={handleReset}
              style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 13, cursor: 'pointer' }}>
              Annuler
            </button>
            {result && <span style={{ fontSize: 12, color: result.startsWith('✓') ? '#15803d' : '#b91c1c' }}>{result}</span>}
          </div>
        </div>
      ) : (
        <pre style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--bg3)', padding: '12px 14px', borderRadius: 8, whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit', lineHeight: 1.6 }}>
          {value}
        </pre>
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
export default function SMS() {
  const [status,    setStatus]    = useState(null);
  const [messages,  setMessages]  = useState([]);
  const [templates, setTemplates] = useState([]);
  const [phoneMap,  setPhoneMap]  = useState({});
  const [loading,   setLoading]   = useState(true);
  const [mainTab,   setMainTab]   = useState('messages');
  const [msgTab,    setMsgTab]    = useState('all');
  const [testTo,    setTestTo]    = useState('');
  const [testMsg,   setTestMsg]   = useState('Test VALEA — message de vérification.');
  const [sending,   setSending]   = useState(false);
  const [sendResult, setSendResult] = useState('');
  const [disconnecting, setDisconnecting] = useState(false);

  const loadAll = async () => {
    try {
      const [st, msgs, tpls, tech] = await Promise.all([
        getWAStatus(), getWAMessages(), getWATemplates(), getTechniciens(),
      ]);
      setStatus(st);
      setMessages(msgs.messages || []);
      setTemplates(tpls.templates || []);
      setPhoneMap(buildPhoneMap(tech?.techniciens || []));
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    const iv = setInterval(() => {
      getWAStatus().then(st => {
        setStatus(prev => {
          if (prev?.status !== 'connected' && st.status === 'connected') loadAll();
          return st;
        });
      }).catch(() => {});
    }, status?.status === 'qr_pending' ? 5000 : 15000);
    return () => clearInterval(iv);
  }, [status?.status]);

  const handleDisconnect = async () => {
    if (!window.confirm('Déconnecter WhatsApp ? Un nouveau QR code devra être scanné.')) return;
    setDisconnecting(true);
    try { await disconnectWA(); setTimeout(loadAll, 2000); } catch {}
    setDisconnecting(false);
  };

  const handleSendTest = async () => {
    if (!testTo || !testMsg) return;
    setSending(true); setSendResult('');
    try {
      await sendWATest(testTo, testMsg);
      setSendResult('✓ Message envoyé');
      loadAll();
    } catch (e) { setSendResult('✗ ' + (e.response?.data?.error || e.message)); }
    setSending(false);
  };

  const handleSaveTemplate = async (id, template) => {
    await updateWATemplate(id, template);
    const updated = await getWATemplates();
    setTemplates(updated.templates || []);
  };

  const handleResend = async (msg) => {
    const to = msg.to.replace('@s.whatsapp.net', '');
    await sendWATest(to, msg.text);
    setTimeout(loadAll, 1500);
  };

  const [resendingAll, setResendingAll] = useState(false);
  const [resendAllResult, setResendAllResult] = useState('');
  const handleResendAll = async () => {
    const pending = messages.filter(m => m.status === 'queued' || m.status === 'error');
    if (!pending.length) return;
    setResendingAll(true); setResendAllResult('');
    let ok = 0;
    for (const m of pending) {
      try {
        await sendWATest(m.to.replace('@s.whatsapp.net', ''), m.text);
        ok++;
      } catch {}
      await new Promise(r => setTimeout(r, 1200));
    }
    setResendAllResult(`✓ ${ok}/${pending.length} renvoyé${ok > 1 ? 's' : ''}`);
    setResendingAll(false);
    setTimeout(() => { setResendAllResult(''); loadAll(); }, 3000);
  };

  const filtered = msgTab === 'all' ? messages : messages.filter(m => m.status === msgTab);
  const todayCount = messages.filter(m => isToday(m.ts) && m.status === 'sent').length;
  const errorCount = messages.filter(m => m.status === 'error').length;
  const queueCount = messages.filter(m => m.status === 'queued').length;

  const StatusBadge = () => {
    if (!status) return null;
    if (status.status === 'connected') return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#15803d' }}>Connecté</span>
        {status.phone && <span style={{ fontSize: 11, color: '#166534', fontFamily: 'monospace' }}>+{status.phone}</span>}
      </div>
    );
    if (status.status === 'qr_pending') return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', animation: 'pulse 1.5s infinite' }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#b45309' }}>QR à scanner</span>
      </div>
    );
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>Déconnecté</span>
      </div>
    );
  };

  return (
    <>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      <div className="topbar">
        <div>
          <div className="topbar-title">💬 WhatsApp</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Baileys — auto-hébergé</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusBadge />
          <button className="btn btn-secondary btn-sm" onClick={loadAll} disabled={loading}>{loading ? '⏳' : '↻'}</button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* QR Code */}
        {status?.status === 'qr_pending' && status.qrDataUrl && (
          <div style={{ background: 'var(--bg2)', border: '2px solid #fde68a', borderRadius: 14, padding: '24px', display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <img src={status.qrDataUrl} alt="QR" style={{ width: 240, height: 240, borderRadius: 10, border: '1px solid var(--border)' }} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>Scanner le QR avec WhatsApp</div>
              <ol style={{ paddingLeft: 18, fontSize: 14, color: 'var(--text2)', lineHeight: 2.2 }}>
                <li>Ouvrir WhatsApp</li>
                <li>⋮ <strong>3 points</strong> → <strong>Appareils connectés</strong></li>
                <li><strong>Connecter un appareil</strong></li>
                <li>Scanner le QR ci-contre</li>
              </ol>
              <div style={{ fontSize: 12, color: 'var(--text3)', background: 'var(--bg3)', padding: '8px 12px', borderRadius: 8, marginTop: 8 }}>
                ⏱ Page rafraîchie automatiquement toutes les 5 secondes
              </div>
            </div>
          </div>
        )}

        {/* Tabs principaux */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg3)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
          {[['messages', '📨 Messages'], ['automations', '⚙️ Automatisations']].map(([key, label]) => (
            <button key={key} onClick={() => setMainTab(key)} style={{
              padding: '8px 20px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: mainTab === key ? 'var(--bg2)' : 'transparent',
              color: mainTab === key ? 'var(--accent)' : 'var(--text3)',
              boxShadow: mainTab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            }}>{label}</button>
          ))}
        </div>

        {/* ═══ ONGLET MESSAGES ═══ */}
        {mainTab === 'messages' && (
          <>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
              {[
                { label: "Aujourd'hui", val: todayCount, color: '#22c55e' },
                { label: 'Total', val: messages.length, color: 'var(--text)', tab: 'all' },
                { label: 'En attente', val: queueCount, color: '#b45309', tab: 'queued' },
                { label: 'Erreurs', val: errorCount, color: '#b91c1c', tab: 'error' },
              ].map(({ label, val, color, tab }) => (
                <div key={label} onClick={() => tab && setMsgTab(tab)}
                  style={{ background: 'var(--bg2)', border: `1px solid ${tab && msgTab === tab ? color : 'var(--border)'}`, borderRadius: 12, padding: '14px 18px', cursor: tab ? 'pointer' : 'default' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{label}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Envoi test */}
            {status?.status === 'connected' && (
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Envoyer un message test</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <input type="text" placeholder="Numéro (ex: 0612345678 ou 33612345678)"
                    value={testTo} onChange={e => setTestTo(e.target.value)}
                    style={{ flex: '0 0 240px', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg)', color: 'var(--text)' }} />
                  <input type="text" placeholder="Message"
                    value={testMsg} onChange={e => setTestMsg(e.target.value)}
                    style={{ flex: 1, minWidth: 180, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg)', color: 'var(--text)' }} />
                  <button className="btn btn-primary btn-sm" onClick={handleSendTest} disabled={sending || !testTo || !testMsg}>
                    {sending ? 'Envoi…' : 'Envoyer'}
                  </button>
                </div>
                {sendResult && <div style={{ marginTop: 8, fontSize: 12, color: sendResult.startsWith('✓') ? '#15803d' : '#b91c1c' }}>{sendResult}</div>}
              </div>
            )}

            {/* Liste messages */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 4, background: 'var(--bg3)', borderRadius: 10, padding: 4 }}>
                  {[['all','Tous'], ['sent','Envoyés'], ['queued','En attente'], ['error','Erreurs']].map(([key, label]) => (
                    <button key={key} onClick={() => setMsgTab(key)} style={{
                      padding: '6px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      background: msgTab === key ? 'var(--bg2)' : 'transparent',
                      color: msgTab === key ? 'var(--accent)' : 'var(--text3)',
                    }}>{label}</button>
                  ))}
                </div>
                {(queueCount > 0 || errorCount > 0) && status?.status === 'connected' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={handleResendAll} disabled={resendingAll}
                      style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #b45309', background: '#fefce8', color: '#b45309', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                      {resendingAll ? 'Envoi en cours…' : `↻ Renvoyer tout (${queueCount + errorCount})`}
                    </button>
                    {resendAllResult && <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>{resendAllResult}</span>}
                  </div>
                )}
              </div>
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10 }}>
                {filtered.length === 0
                  ? <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Aucun message</div>
                  : filtered.map((msg, i) => <MsgCard key={i} msg={msg} phoneMap={phoneMap} onResend={handleResend} />)
                }
              </div>
            </div>

            {/* Déconnexion */}
            <div style={{ background: 'var(--bg2)', border: '1px solid #fecaca', borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c', marginBottom: 6 }}>Zone danger</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>Déconnecter supprime la session et génère un nouveau QR à scanner.</div>
              <button onClick={handleDisconnect} disabled={disconnecting}
                style={{ padding: '7px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                {disconnecting ? 'Déconnexion…' : '⚠ Déconnecter WhatsApp'}
              </button>
            </div>
          </>
        )}

        {/* ═══ ONGLET AUTOMATISATIONS ═══ */}
        {mainTab === 'automations' && (
          <>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#1e40af' }}>
              <strong>{templates.length} automatisations programmées</strong> — chacune a son propre déclencheur et sa propre heure, indiqués sur sa fiche.<br />
              Cliquez <strong>Modifier</strong> pour éditer un template. Les variables entre <code style={{ background: '#dbeafe', padding: '0 4px', borderRadius: 4 }}>{'{{  }}'}</code> sont remplacées automatiquement.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {templates.map(tpl => (
                <TemplateCard key={tpl.id} tpl={tpl} onSave={handleSaveTemplate} />
              ))}
            </div>
          </>
        )}

      </div>
    </>
  );
}
